/**
 * Movimiento del catálogo editorial.
 *
 * Dos efectos, ambos deliberadamente contenidos:
 *
 *  - Luz que sigue al cursor sobre cada imagen. Un resplandor muy tenue que
 *    sugiere volumen sin alterar el color de la fotografía.
 *  - Parallax de respaldo para navegadores sin animaciones ligadas al scroll.
 *    Donde `animation-timeline: view()` existe, el efecto lo resuelve el CSS y
 *    este JS ni se activa: el compositor lo hace mejor que cualquier listener.
 *
 * Todo se apaga con prefers-reduced-motion y en punteros no finos: en táctil no
 * hay cursor que seguir y el parallax sólo añade trabajo a la GPU.
 */

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const supportsScrollTimeline = CSS.supports('animation-timeline', 'view()');

/* --- Luz que sigue al cursor ---------------------------------------------- */

function bindLight(el) {
  let frame = null;

  el.addEventListener('pointermove', (event) => {
    if (frame) return; // como mucho una actualización por frame
    frame = requestAnimationFrame(() => {
      frame = null;
      const rect = el.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--sc-mx', `${x}%`);
      el.style.setProperty('--sc-my', `${y}%`);
    });
  });

  el.addEventListener('pointerleave', () => {
    el.style.removeProperty('--sc-mx');
    el.style.removeProperty('--sc-my');
  });
}

/* --- Parallax de respaldo -------------------------------------------------- */

let parallaxTargets = [];
let ticking = false;

function updateParallax() {
  ticking = false;
  const vh = window.innerHeight;

  for (const el of parallaxTargets) {
    const rect = el.parentElement.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > vh) continue;

    // -1 arriba del viewport, +1 abajo. El desplazamiento es proporcional.
    const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2);
    const shift = Math.max(-1, Math.min(1, progress)) * 6;
    el.style.transform = `translate3d(0, ${shift}%, 0) scale(1.12)`;
  }
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(updateParallax);
}

/* --- Arranque -------------------------------------------------------------- */

/* --- El scroll como cursor, en navegadores sin scroll-driven animations ----
 *
 * Marca la fotografía que ocupa el centro de la pantalla para que revele su
 * nombre y precio. Sólo se activa donde `animation-timeline: view()` no existe;
 * donde sí, lo resuelve el CSS y esto ni se registra.
 *
 * La banda de detección es una franja estrecha en mitad del viewport, no el
 * viewport entero: si fuera todo, en móvil habría tres o cuatro fotos activas a
 * la vez y el efecto perdería el sentido de "estoy mirando ésta".
 */
function bindCentered(root) {
  const looks = [...root.querySelectorAll('.sc-look:not([data-centered-bound])')];
  if (looks.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        entry.target.classList.toggle('is-centered', entry.isIntersecting);
      }
    },
    /* Franja del 16% en mitad de la pantalla.

       Se probó con el 30% y revelaba cuatro fotos a la vez, que era justo lo
       que se quería evitar. Estrecha, sólo entra la que domina el centro. */
    { rootMargin: '-42% 0px -42% 0px', threshold: 0 }
  );

  looks.forEach((el) => {
    el.setAttribute('data-centered-bound', '');
    observer.observe(el);
  });
}

function init(root = document) {
  if (reduced.matches) return;

  /* En táctil siempre, con o sin soporte de scroll-driven animations: el
     control de cuántas fotos se revelan a la vez sólo lo da el observador. */
  if (!finePointer.matches) {
    bindCentered(root);
  }

  if (finePointer.matches) {
    root.querySelectorAll('[data-light]:not([data-light-bound])').forEach((el) => {
      el.setAttribute('data-light-bound', '');
      bindLight(el);
    });
  }

  if (supportsScrollTimeline) return; // el CSS ya lo resuelve

  const found = [...root.querySelectorAll('[data-parallax]')];
  if (found.length === 0) return;

  parallaxTargets = [...new Set([...parallaxTargets, ...found])];

  if (parallaxTargets.length === found.length) {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  updateParallax();
}

init();

// El editor de temas inyecta secciones sin recargar.
document.addEventListener('shopify:section:load', (event) => init(event.target));
